// src/components/home/UniformsSection.jsx
import React, { useState } from 'react'
import { X, ZoomIn } from 'lucide-react'
import Button from '../ui/Button'

const UniformsSection = () => {
  const [selectedImage, setSelectedImage] = useState(null)

  const uniforms = [
    {
      id: 1,
      src: "/images/uniform-male.png",
      alt: "Uniforme Masculino - IED Las Gardenias",
      title: "Uniforme Masculino"
    },
    {
      id: 2,
      src: "/images/uniform-female.png",
      alt: "Uniforme Femenino - IED Las Gardenias",
      title: "Uniforme Femenino"
    },
    {
      id: 3,
      src: "/images/uniform-complete.png",
      alt: "Uniforme Completo - IED Las Gardenias",
      title: "Uniforme Completo"
    }
  ]

  const openImage = (image) => {
    setSelectedImage(image)
    document.body.style.overflow = 'hidden'
  }

  const closeImage = () => {
    setSelectedImage(null)
    document.body.style.overflow = 'auto'
  }

  return (
    <>
      <section className="section-padding bg-gradient-to-b from-white to-gardenia-gold/10">
        <div className="container-custom">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gardenia-dark mb-6">
              Nuestro
              <span className="text-gradient block mt-2">Uniforme Escolar</span>
            </h2>
            <div className="max-w-3xl mx-auto mb-8">
              <p className="text-lg md:text-xl text-gardenia-red font-semibold italic leading-relaxed mb-4">
                "Vestir el uniforme con orgullo es reflejo de disciplina, pertenencia y excelencia personal"
              </p>
              <p className="text-base text-gray-600">
                El uniforme representa nuestra identidad institucional y fortalece el sentido de pertenencia 
                a la comunidad educativa de Las Gardenias.
              </p>
            </div>
          </div>

          {/* Uniforms Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {uniforms.map((uniform) => (
              <div
                key={uniform.id}
                className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer"
                onClick={() => openImage(uniform)}
              >
                {/* Image Container */}
                <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-b from-gardenia-gold/5 to-white">
                  <img
                    src={uniform.src}
                    alt={uniform.alt}
                    className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500"
                  />
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform group-hover:scale-100 scale-90">
                      <div className="bg-white rounded-full p-4 shadow-xl">
                        <ZoomIn className="h-8 w-8 text-gardenia-red" />
                      </div>
                      <p className="text-white font-semibold mt-3 text-center">Ver detalle</p>
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="p-4 bg-gradient-to-r from-gardenia-red to-gardenia-green">
                  <h3 className="text-lg font-bold text-white text-center">
                    {uniform.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>

          {/* Uniform Details */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gardenia-gold/30">
            <h3 className="text-2xl font-bold text-gardenia-dark mb-6 text-center">
              Componentes del Uniforme
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Column */}
              <div>
                <h4 className="text-lg font-semibold text-gardenia-red mb-4 flex items-center">
                  <span className="w-2 h-2 bg-gardenia-red rounded-full mr-3"></span>
                  Parte Superior
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-gardenia-green mr-2">✓</span>
                    <span>Camiseta blanca con cuello en V y detalles beige</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gardenia-green mr-2">✓</span>
                    <span>Escudo institucional bordado en el pecho</span>
                  </li>
                </ul>
              </div>

              {/* Right Column */}
              <div>
                <h4 className="text-lg font-semibold text-gardenia-red mb-4 flex items-center">
                  <span className="w-2 h-2 bg-gardenia-red rounded-full mr-3"></span>
                  Parte Inferior y Calzado
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-gardenia-green mr-2">✓</span>
                    <span>Pantalón beige con logo institucional lateral</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gardenia-green mr-2">✓</span>
                    <span><strong>Tenis totalmente blancos</strong> (sin detalles de otros colores)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gardenia-green mr-2">✓</span>
                    <span>Medias blancas</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Important Note */}
            <div className="mt-8 p-4 bg-gardenia-gold/10 border-l-4 border-gardenia-gold rounded-r-lg">
              <p className="text-gardenia-dark">
                <strong className="text-gardenia-red">Nota importante:</strong> El uso correcto del uniforme 
                es obligatorio para todos los estudiantes. Los tenis deben ser completamente blancos, 
                sin logos o detalles de otros colores.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              Para más información sobre el uniforme y puntos de venta autorizados
            </p>
            <Button to="/contacto" size="lg">
              Contáctanos
            </Button>
          </div>
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-fade-in"
          onClick={closeImage}
        >
          {/* Close Button */}
          <button
            onClick={closeImage}
            className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all duration-300 z-10"
            aria-label="Cerrar imagen"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Image Container */}
          <div
            className="relative max-w-4xl max-h-[90vh] animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="w-full h-full object-contain rounded-lg shadow-2xl"
            />
            
            {/* Image Title */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
              <h3 className="text-white text-xl font-bold text-center">
                {selectedImage.title}
              </h3>
            </div>
          </div>

          {/* Hint Text */}
          <div className="absolute bottom-8 left-0 right-0 text-center">
            <p className="text-white/70 text-sm">
              Haz clic fuera de la imagen o presiona ESC para cerrar
            </p>
          </div>
        </div>
      )}
    </>
  )
}

export default UniformsSection